/**
 * Decode a user-supplied File/Blob into ImageData (RGBA, sRGB) for analysis.
 *
 * Uses `createImageBitmap` for fast hardware-accelerated decode where
 * available, falls back to an `<img>` + canvas roundtrip otherwise. Resized
 * down to a working size to keep ORB cost bounded — marker authoring images
 * can be 20 MP and ORB on 20 MP is gratuitously slow.
 */

import type { ImageFileInfo, ImageFormat } from './types';

/** Long-edge cap for analysis. ORB results saturate well before this. */
const MAX_ANALYSIS_DIMENSION = 1600;

export interface DecodedImage {
  /** Original (pre-resize) dimensions, for accurate resolution reporting. */
  originalWidth: number;
  originalHeight: number;
  /** Working image data — possibly downscaled. */
  data: ImageData;
}

export async function decodeImageForAnalysis(input: File | Blob): Promise<DecodedImage> {
  const bitmap = await createImageBitmap(input);
  const originalWidth = bitmap.width;
  const originalHeight = bitmap.height;

  const longEdge = Math.max(originalWidth, originalHeight);
  const scale = longEdge > MAX_ANALYSIS_DIMENSION ? MAX_ANALYSIS_DIMENSION / longEdge : 1;
  const width = Math.max(1, Math.round(originalWidth * scale));
  const height = Math.max(1, Math.round(originalHeight * scale));

  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(width, height)
    : Object.assign(document.createElement('canvas'), { width, height });
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Failed to acquire 2D canvas context for image analysis');

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const data = ctx.getImageData(0, 0, width, height);

  return { originalWidth, originalHeight, data };
}

/** Read original pixel dimensions without resizing. Used purely for reporting. */
export async function readOriginalDimensions(input: File | Blob): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(input);
  const dims = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return dims;
}

export function classifyImageFormat(mimeType: string, fileName: string | undefined): ImageFormat {
  const mt = mimeType.toLowerCase();
  if (mt === 'image/jpeg' || mt === 'image/jpg') return 'jpeg';
  if (mt === 'image/png') return 'png';
  if (mt === 'image/webp') return 'webp';
  if (mt === 'image/gif') return 'gif';
  if (mt === 'image/bmp') return 'bmp';
  if (mt === 'image/avif') return 'avif';
  if (fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'jpg' || ext === 'jpeg') return 'jpeg';
    if (ext === 'png') return 'png';
    if (ext === 'webp') return 'webp';
    if (ext === 'gif') return 'gif';
    if (ext === 'bmp') return 'bmp';
    if (ext === 'avif') return 'avif';
  }
  return 'unknown';
}

export function makeFileInfo(
  input: File | Blob,
  decoded: DecodedImage,
): ImageFileInfo {
  const name = input instanceof File ? input.name : undefined;
  return {
    format: classifyImageFormat(input.type, name),
    mimeType: input.type || 'application/octet-stream',
    fileSize: input.size,
    width: decoded.originalWidth,
    height: decoded.originalHeight,
  };
}
